// UI data for the site. Facts (dates, amounts, FAQ text, links, sources) live in content/facts.ts; this file only
// arranges them for components and adds UI-only copy such as checklist steps.
import { faq, inHouseConnect, phases, urls, type Claim, type Phase } from '../content/facts'
import { to } from './routes'

export * from '../content/facts'

const fq = (id: string) => faq.find(f => f.id === id)!.claims
/** The sourced short answer under each page's title (PageHead `answer`), quoted from the FAQ. */
export const pageAnswers: Record<string, Claim[]> = {
  '/when': fq('who-and-when').slice(0, 2),
  '/how-it-works': fq('what-is-it').slice(0, 2),
}

/** The day the site was built (Singapore time). All relative counters use this, so prerendered HTML and the client agree. */
export const BUILD_DATE: string = __BUILD_DATE__

export const flowData: [string, string][] = [
  ['Create', 'You issue an invoice in your InvoiceNow-Ready Solution, the accounting or invoicing software you already use.'],
  ['Send', 'Your Access Point checks the invoice against the Peppol format and sends it onto the network.'],
  ['Receive', "Your buyer's Access Point receives the invoice and passes it on."],
  ['Deliver', "The invoice lands in your buyer's system as structured data. No PDF, no re-keying."],
  ['Report', 'Invoice data is transmitted to IRAS through the InvoiceNow network.'],
]

/** Whole months from `today` to `iso` (ISO dates): null once in effect, 0 if under a month away. */
export function monthsUntil(iso: string, today: string = BUILD_DATE): number | null {
  if (iso <= today) return null
  const [y1, m1, d1] = today.split('-').map(Number)
  const [y2, m2, d2] = iso.split('-').map(Number)
  return Math.max(0, (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0))
}

/** English form ("18 MONTHS AWAY", "IN EFFECT"); components use the translated until.* strings instead. */
export function until(iso: string, today: string = BUILD_DATE): string {
  const m = monthsUntil(iso, today)
  if (m === null) return 'IN EFFECT'
  if (m < 1) return 'UNDER A MONTH AWAY'
  return m === 1 ? '1 MONTH AWAY' : `${m} MONTHS AWAY`
}

export const isInEffect = (p: Phase, today: string = BUILD_DATE) => p.startIso <= today
export const nextPhase = (today: string = BUILD_DATE) => phases.find(p => !isInEffect(p, today))

/** Checklist steps: text lives in locales/<code>/ui.json under checklist.step.<i>.* ; this holds links only.
 *  Each inner array is one row of buttons: [href, filled?]. Labels are checklist.step.<i>.a.<n> in order, so add new
 *  buttons at the end of a step to keep existing translations on the right button. Every step links to a page or
 *  tool on this site as well as the official source. */
export type NowAction = [href: string, filled?: boolean]
/** `tool` embeds an interactive tool in the step's panel, above its buttons ('peppol': the Peppol ID search). */
export type NowTool = 'peppol'
/** Where each embedded tool also lives on its own; the step's on-site link in HowTo data and llms-full.txt. */
export const nowToolPage: Record<NowTool, string> = { peppol: to('peppol-id') }
export const nowSteps: { actions: NowAction[][]; tool?: NowTool }[] = [
  { actions: [[[to('calculator'), true], [to('timeline')]]] },
  { actions: [[[urls.irspList], [urls.apList]], [[to('find-vendor'), true], [to('pos')], [to('choose')]]] },
  { tool: 'peppol', actions: [[[urls.irspContact], [to('what-you-need')]]] },
  { actions: [[[to('how'), true]], [[to('faq')]]] },
  { actions: [[[urls.etg, true], [to('events')]], [['/links']]] },
]
export const nowDone = { actions: [[[to('grants'), true], [to('events')], [to('question')]]] as NowAction[][] }
/** Months for the in-house connection line in checklist step 5. */
export const inHouseMonths = { from: inHouseConnect.from, to: inHouseConnect.to }
